import { db } from "src/database/dexie";
import { getLocalDate, parseLocalDate } from "src/database/utils/localDate";
import { eachDayOfInterval } from "date-fns";
import { Habit, InstanceOverride, Instance } from "src/database/types";
import { Instances } from "src/database/services/Instances";
import { Habits } from "src/database/services/Habits";
import { v5 as uuidv5 } from "uuid";

export const HabitInstantiator = {
  getInstancesByDateRange,
  getInstancesByDate,
  templateToInstances,
};

export function templateToInstances(
  date: string,
  template: Habit[],
  storedInstances: InstanceOverride[],
): Instance[] {
  const instances: Instance[] = [];
  const storedInstanceIndex: {
    [key: string]: Omit<InstanceOverride, "parentId">;
  } = {};
  for (const { parentId: _parentId, ...instance } of storedInstances) {
    storedInstanceIndex[instance.templateId] = instance;
  }
  const idMap: { [key: string]: string } = {};
  for (const { id: templateId, ...task } of template) {
    const storedInstance =
      templateId in storedInstanceIndex ? storedInstanceIndex[templateId] : undefined;
    const newId = storedInstance ? storedInstance.id : uuidv5(date, templateId);
    idMap[templateId] = newId;
    instances.push({
      ...task,
      type: "instance",
      id: newId,
      date,
      templateId: templateId,
      completed: false,
      ...storedInstance,
    });
  }

  for (const instance of instances) {
    if (instance.parentId) {
      instance.parentId = idMap[instance.parentId];
    }
  }

  return topoSort(instances);
}

function topoSort(instances: Instance[]): Instance[] {
  // Build a map for quick lookup
  const instanceMap = new Map<string, Instance>();
  for (const instance of instances) {
    instanceMap.set(instance.id, instance);
  }

  // Calculate depth for each instance
  const getDepth = (instance: Instance): number => {
    if (!instance.parentId) return 0;
    const parent = instanceMap.get(instance.parentId);
    return parent ? getDepth(parent) + 1 : 0;
  };

  // Sort by depth (parents before children)
  return [...instances].sort((a, b) => getDepth(a) - getDepth(b));
}

async function getInstancesByDateRange(startDate: string, endDate: string) {
  const dateRange: string[] = eachDayOfInterval({
    start: parseLocalDate(startDate),
    end: parseLocalDate(endDate),
  }).map((date) => getLocalDate(date));

  if (dateRange.length === 0) {
    console.warn("Empty dateRange, returning empty array");
    return [];
  }

  return await db.transaction("r", [db.instances, db.habits], async () => {
    const instancesByDate = await Instances.getByDateRange(dateRange).groupBy("date");

    const template = await Habits.getAll().toArray();
    const instancesByDateArray: Instance[][] = [];
    for (const date of dateRange) {
      const instances: Instance[] = templateToInstances(
        date,
        template,
        instancesByDate[date] ?? [],
      );
      instancesByDateArray.push(instances);
    }

    return instancesByDateArray;
  });
}

async function getInstancesByDate(date: string) {
  const instances = await getInstancesByDateRange(date, date);
  return instances.length > 0 ? instances[0] : [];
}
